/**
 * ElevenLabs TTS Provider
 * Extracted and adapted from Phantasy AudioService
 */

import { BaseTTSProvider } from './ITTSProvider';
import { createLogger } from '../../utils/logger';
import type { TTSProviderConfig } from '../../types/config';

export class ElevenLabsProvider extends BaseTTSProvider {
  private logger: ReturnType<typeof createLogger>;

  constructor(config: TTSProviderConfig) {
    super(config);
    this.logger = createLogger('ElevenLabsProvider');
  }

  getProviderName(): string {
    return 'elevenlabs';
  }

  async generateAudio(text: string): Promise<Blob> {
    try {
      this.logger.info('Generating audio with ElevenLabs for text:', text);

      // Check if we have a valid API key
      if (!this.config.apiKey || this.config.apiKey === 'your_api_key_here') {
        throw new Error('ElevenLabs API key is missing or placeholder');
      }

      // Log masked API key for debugging
      const maskedKey = this.config.apiKey.substring(0, 4) + 
                       '...' + 
                       this.config.apiKey.substring(this.config.apiKey.length - 4);
      this.logger.debug(`Using ElevenLabs API key: ${maskedKey}`);

      // Ensure we have a valid voice ID
      if (!this.config.voiceId) {
        throw new Error('Voice ID is required for ElevenLabs');
      }

      const cleanedText = this.validateText(text);

      const url = `${this.config.apiUrl || 'https://api.elevenlabs.io/v1/text-to-speech'}/${this.config.voiceId}`;
      this.logger.debug('Using ElevenLabs URL:', url);

      const response = await fetch(url, {
        method: 'POST',
        headers: {
          Accept: 'audio/mpeg',
          'Content-Type': 'application/json',
          'xi-api-key': this.config.apiKey,
        },
        body: JSON.stringify({
          text: cleanedText,
          model_id: this.config.model || 'eleven_monolingual_v1',
          voice_settings: {
            stability: this.config.stability || 0.5,
            similarity_boost: this.config.similarityBoost || 0.5,
          },
        }),
      });

      if (!response.ok) {
        const errorText = await response.text().catch(() => 'No error details available');
        throw new Error(`ElevenLabs API error (${response.status}): ${errorText}`);
      }

      const arrayBuffer = await response.arrayBuffer();
      this.logger.info('ElevenLabs audio generated successfully');
      return new Blob([arrayBuffer], { type: 'audio/mpeg' });

    } catch (error) {
      this.logger.error('Error in ElevenLabs generation:', error);
      throw error;
    }
  }
}